import {
  takeLatest, put, select, takeEvery,
} from 'redux-saga/effects';

import DocumentService from '../../../services/DocumentService';
import { apiCall } from '../../effects';
import { getErrorNotification } from '../../util';
import { notify } from '../notifications/actions';
import { selectDocumentForms } from './selectors';
import {
  initDocumentRequest,
  initDocumentSuccess,
  initDocumentFail,
  getDocumentRequest,
  getDocumentSuccess,
  getDocumentFail,
  getDocumentListRequest,
  getDocumentListSuccess,
  getDocumentListFail,
  createDocumentRequest,
  createDocumentSuccess,
  createDocumentFail,
  saveDocumentRequest,
  saveDocumentSuccess,
  saveDocumentFail,
  deleteDocumentRequest,
  deleteDocumentSuccess,
  deleteDocumentFail,
  getDocumentUploadUrlRequest,
  getDocumentUploadUrlSuccess,
  getDocumentUploadUrlFail,
  syncDocumentRequest,
  syncDocumentSuccess,
  syncDocumentFail,
  syncTypesDocumentRequest,
  syncTypesDocumentSuccess,
  syncTypesDocumentFail,
  setDocumentListDirty,
  submitDocumentForm,
  resetDocumentForm,
  removeDocumentForm,
  setDocumentFormsSubmit,
  setDocumentFormsReset,
} from './actions';

function* getDocument({ payload }: ReturnType<typeof getDocumentRequest>) {
  try {
    const response = yield apiCall(DocumentService.get, payload);
    yield put(getDocumentSuccess(response));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(getDocumentFail());
  }
}

function* getDocumentList({ payload }: ReturnType<typeof getDocumentListRequest>) {
  try {
    const list = yield apiCall(DocumentService.list, payload);
    yield put(getDocumentListSuccess(list));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(getDocumentListFail());
  }
}

function* saveDocument({ payload }: ReturnType<typeof saveDocumentRequest>) {
  try {
    const response = yield apiCall(DocumentService.save, payload);
    yield put(saveDocumentSuccess(response));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(saveDocumentFail());
  }
}

function* createDocument({ payload }: ReturnType<typeof createDocumentRequest>) {
  try {
    const response = yield apiCall(DocumentService.create, payload);
    yield put(createDocumentSuccess(response));
  } catch (e) {
    yield put(initDocumentSuccess(payload));
    yield put(notify(getErrorNotification(e)));
    yield put(createDocumentFail());
  }
}

function* deleteDocument({ payload }: ReturnType<typeof deleteDocumentRequest>) {
  try {
    yield apiCall(DocumentService.delete, payload);
    yield put(deleteDocumentSuccess(payload));
    yield put(setDocumentListDirty(true));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(deleteDocumentFail());
  }
}

function* getTemplateDocument() {
  try {
    const response = yield apiCall(DocumentService.template);
    yield put(initDocumentSuccess(response));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(initDocumentFail());
  }
}

function* documentUploadUrl({ payload }: ReturnType<typeof getDocumentUploadUrlRequest>) {
  try {
    const response = yield apiCall(DocumentService.uploadUrl, payload);
    yield put(getDocumentUploadUrlSuccess(response));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(getDocumentUploadUrlFail());
  }
}

function* syncDocument({ payload }: ReturnType<typeof syncDocumentRequest>) {
  try {
    const response = yield apiCall(DocumentService.sync, payload);
    yield put(syncDocumentSuccess(response));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(syncDocumentFail());
  }
}

function* syncTypesDocument() {
  try {
    const response = yield apiCall(DocumentService.syncTypes);
    yield put(syncTypesDocumentSuccess(response));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(syncTypesDocumentFail());
  }
}

function* submitForms({ payload }: ReturnType<typeof submitDocumentForm>) {
  try {
    yield put(removeDocumentForm(payload.id));
    const forms = yield select(selectDocumentForms);
    payload.callback();
    if (forms.forms.length === 0) {
      yield put(setDocumentFormsSubmit(false));
    }
  } catch (e) {
    console.log(e);
  }
}

function* resetForms({ payload }: ReturnType<typeof resetDocumentForm>) {
  try {
    yield put(removeDocumentForm(payload.id));
    payload.callback();
    const forms = yield select(selectDocumentForms);
    if (forms.forms.length === 0) {
      yield put(setDocumentFormsReset(false));
    }
  } catch (e) {
    console.log(e);
  }
}


export default [
  takeLatest(initDocumentRequest.type, getTemplateDocument),
  takeLatest(getDocumentRequest.type, getDocument),
  takeLatest(getDocumentListRequest.type, getDocumentList),
  takeLatest(createDocumentRequest.type, createDocument),
  takeEvery(saveDocumentRequest.type, saveDocument),
  takeLatest(deleteDocumentRequest.type, deleteDocument),
  takeLatest(getDocumentUploadUrlRequest.type, documentUploadUrl),
  takeLatest(syncDocumentRequest.type, syncDocument),
  takeLatest(syncTypesDocumentRequest.type, syncTypesDocument),
  takeLatest(submitDocumentForm.type, submitForms),
  takeLatest(resetDocumentForm.type, resetForms),
];
